import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Clock, CalendarDays, IndianRupee, AlertCircle, Mail } from 'lucide-react';

function fmtTime(iso) { if (!iso) return '—'; return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
function inr(n) { if (n == null) return '—'; return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }

const statusStyles = {
  verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending_onboarding: 'bg-slate-100 text-slate-600 border-slate-200',
  rm_review: 'bg-blue-50 text-blue-700 border-blue-200',
  hr_review: 'bg-amber-50 text-amber-700 border-amber-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

export default function MyTeam() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/my-team'); setTeam(data); }
      catch (e) { /* may 403 for non-RM */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="my-team-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Reports</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">My Team</h1>
        <p className="text-slate-500 mt-1">{team.length} direct {team.length === 1 ? 'report' : 'reports'} · live snapshot</p>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">Loading…</div>
      ) : team.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400 flex flex-col items-center gap-3">
          <AlertCircle className="w-10 h-10 text-slate-300" />
          <div>No reports assigned to you yet.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {team.map(m => (
            <div key={m.id} className="bg-white border border-slate-200 rounded-2xl p-6" data-testid={`team-member-${m.id}`}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-14 h-14 rounded-xl bg-[#3D4A8E] text-white flex items-center justify-center font-heading text-xl font-bold flex-shrink-0">
                    {m.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-lg text-slate-900 truncate">{m.name}</div>
                    <div className="text-xs text-slate-500 truncate">{m.title || '—'} · {m.department}</div>
                    <div className="text-xs text-slate-400 truncate flex items-center gap-1 mt-1"><Mail className="w-3 h-3" /> {m.email}</div>
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${statusStyles[m.onboarding_status] || statusStyles.verified}`}>
                  {m.onboarding_status === 'verified' ? 'Active' : m.onboarding_status?.replace('_', ' ')}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Stat icon={Clock} label="Today's check-in" v={fmtTime(m.attendance_today.check_in)} sub={m.attendance_today.check_out ? `Out: ${fmtTime(m.attendance_today.check_out)}` : (m.attendance_today.check_in ? 'In office' : 'Not yet')} />
                <Stat icon={CalendarDays} label="Pending leaves" v={m.pending_leaves} sub={m.pending_leaves > 0 ? 'Action needed' : 'All clear'} />
                <Stat icon={IndianRupee} label="Latest payslip" v={m.latest_payslip ? inr(m.latest_payslip.net) : '—'} sub={m.latest_payslip?.month || 'No payslip yet'} />
                <Stat icon={CalendarDays} label="Leave balance" v={`${m.leave_balance.remaining.casual + m.leave_balance.remaining.sick + m.leave_balance.remaining.earned}`} sub={`C${m.leave_balance.remaining.casual} · S${m.leave_balance.remaining.sick} · E${m.leave_balance.remaining.earned}`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, v, sub }) => (
  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-slate-400" />
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    </div>
    <div className="font-heading text-lg font-bold text-slate-900 mt-1">{v}</div>
    <div className="text-xs text-slate-500">{sub}</div>
  </div>
);
